import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { inverterDataAPI, temperatureDataAPI, channelDataAPI, inverterErrorAPI } from '../../services/api';
import usePeriodicFetch from '../../hooks/usePeriodicFetch';
import './PowerPlantTrendDetail.css';

const PowerPlantTrendDetail = ({ powerPlant, configData, powerLocationId }) => {
  const [trendData, setTrendData] = useState(null);
  const [selectedEquipment, setSelectedEquipment] = useState({ type: null, data: null });
  const [selectedDataType, setSelectedDataType] = useState('dcVoltage');
  // eslint-disable-next-line no-unused-vars
  const [selectedEnvironmentSensor, setSelectedEnvironmentSensor] = useState(null);
  const [selectedTemperatureType, setSelectedTemperatureType] = useState('outsideTemperature');
  const [selectedIrradianceType, setSelectedIrradianceType] = useState('tiltedIrradiance');
  const [inverterRealData, setInverterRealData] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [temperatureData, setTemperatureData] = useState(null);
  const [hourlyTemperatureData, setHourlyTemperatureData] = useState([]);
  const [hourlySolarRadiationData, setHourlySolarRadiationData] = useState([]);
  const [channelData, setChannelData] = useState([]);
  const [channelDataLoading, setChannelDataLoading] = useState(false);
  const [inverterErrorCount, setInverterErrorCount] = useState(0);

  // 인버터 실제 데이터 가져오기
  const fetchInverterData = useCallback(async (inverterId) => {
    try {
      console.log('인버터 데이터 요청 중...', inverterId);
      const data = await inverterDataAPI.getTrendData(inverterId);
      console.log('인버터 데이터 받음:', data);
      setInverterRealData(data);
    } catch (error) {
      console.error('인버터 데이터 가져오기 실패:', error);
      setInverterRealData(null);
    }
  }, []);

  // 차트 데이터 가져오기
  const fetchChartData = useCallback(async (inverterId, chartType) => {
    try {
      console.log('차트 데이터 요청 중...', inverterId, chartType);
      const data = await inverterDataAPI.getChartData(inverterId, chartType);
      console.log('차트 데이터 받음:', data);
      setChartData(data);
    } catch (error) {
      console.error('차트 데이터 가져오기 실패:', error);
      console.error('에러 상세:', error.response?.data);
      console.error('에러 상태:', error.response?.status);
      setChartData([]);
    }
  }, []);

  // 온도 데이터 가져오기
  const fetchTemperatureData = useCallback(async (powerLocationId) => {
    try {
      console.log('온도 데이터 요청 중... powerLocationId:', powerLocationId);
      const data = await temperatureDataAPI.getTemperatureData(powerLocationId);
      console.log('온도 데이터 받음:', data);
      setTemperatureData(data);
    } catch (error) {
      console.error('온도 데이터 가져오기 실패:', error);
      console.error('에러 상세:', error.response?.data);
      console.error('에러 상태:', error.response?.status);
      setTemperatureData(null);
    }
  }, []);

  // 시간별 온도 데이터 가져오기
  const fetchHourlyTemperatureData = useCallback(async (powerLocationId, temperatureType) => {
    try {
      const data = await temperatureDataAPI.getHourlyTemperatureByType(powerLocationId, temperatureType);
      setHourlyTemperatureData(data);
    } catch (error) {
      console.error('[PowerPlantTrendDetail] 시간별 온도 데이터 가져오기 실패:', error);
      setHourlyTemperatureData([]);
    }
  }, []);

  // 시간별 일사량 데이터 가져오기
  const fetchHourlySolarRadiationData = useCallback(async (powerLocationId, radiationType) => {
    try {
      const data = await temperatureDataAPI.getHourlySolarRadiationByType(powerLocationId, radiationType);
      setHourlySolarRadiationData(data);
    } catch (error) {
      console.error('[PowerPlantTrendDetail] 시간별 일사량 데이터 가져오기 실패:', error);
      setHourlySolarRadiationData([]);
    }
  }, []);

  // 채널 데이터 가져오기
  const fetchChannelData = useCallback(async (panelConfigId) => {
    setChannelDataLoading(true);
    try {
      const data = await channelDataAPI.getLatestChannelDataByPanelConfigId(panelConfigId);
      setChannelData(data || []);
    } catch (error) {
      console.error('[PowerPlantTrendDetail] 채널 데이터 가져오기 실패:', error);
      setChannelData([]);
    } finally {
      setChannelDataLoading(false);
    }
  }, []);

  // 인버터 에러 카운트 가져오기
  const fetchInverterErrorCount = useCallback(async (inverterConfigId) => {
    try {
      const count = await inverterErrorAPI.getInverterErrorCount(inverterConfigId);
      setInverterErrorCount(count || 0);
    } catch (error) {
      console.error('[PowerPlantTrendDetail] 인버터 에러 카운트 가져오기 실패:', error);
      setInverterErrorCount(0);
    }
  }, []);


  // 프론트엔드 타입을 백엔드 enum으로 변환
  const getBackendChartType = useCallback((frontendType) => {
    switch (frontendType) {
      case 'dcVoltage': return 'DC_VOLTAGE';
      case 'dcCurrent': return 'DC_CURRENT';
      case 'dcPower': return 'DC_POWER';
      case 'acVoltage': return 'AC_VOLTAGE';
      case 'acCurrent': return 'AC_CURRENT';
      case 'acPower': return 'AC_POWER';
      default: return 'DC_VOLTAGE';
    }
  }, []);

  // 주기적으로 실행할 모든 데이터 가져오기 (10초 간격)
  const fetchAllPeriodicData = useCallback(async () => {
    console.log('[PowerPlantTrendDetail] 주기적 데이터 가져오기 시작');
    
    // 선택된 장비 타입에 따라 다른 데이터 가져오기
    if (selectedEquipment.type === 'inverter' && selectedEquipment.data?.inverterId) {
      // 인버터가 선택된 경우
      await Promise.all([
        fetchInverterData(selectedEquipment.data.inverterId),
        fetchChartData(selectedEquipment.data.inverterId, getBackendChartType(selectedDataType)),
        fetchInverterErrorCount(selectedEquipment.data.inverterId)
      ]);
    } else if (selectedEquipment.type === 'connectionBox' && selectedEquipment.data?.id) {
      // 접속반이 선택된 경우
      await fetchChannelData(selectedEquipment.data.id);
    } else if (selectedEquipment.type === 'environmentSensor' && powerLocationId) {
      // 환경센서가 선택된 경우
      const temperatureType = selectedTemperatureType === 'outsideTemperature' ? 'AMBIENT' : 'SURFACE';
      const radiationType = selectedIrradianceType === 'tiltedIrradiance' ? 'SLOPED' : 'HORIZONTAL';
      
      await Promise.all([
        fetchTemperatureData(powerLocationId),
        fetchHourlyTemperatureData(powerLocationId, temperatureType),
        fetchHourlySolarRadiationData(powerLocationId, radiationType)
      ]);
    }
    
    console.log('[PowerPlantTrendDetail] 주기적 데이터 가져오기 완료');
  }, [
    selectedEquipment.type, 
    selectedEquipment.data?.inverterId, 
    selectedEquipment.data?.id, 
    selectedDataType,
    selectedTemperatureType,
    selectedIrradianceType,
    powerLocationId,
    fetchInverterData,
    fetchChartData,
    fetchInverterErrorCount,
    fetchChannelData,
    fetchTemperatureData,
    fetchHourlyTemperatureData,
    fetchHourlySolarRadiationData,
    getBackendChartType
  ]);

  // 10초 간격으로 주기적 데이터 가져오기
  usePeriodicFetch(
    fetchAllPeriodicData,
    10000, // 10초
    [selectedEquipment.type, selectedEquipment.data?.inverterId, selectedEquipment.data?.id, selectedDataType, selectedTemperatureType, selectedIrradianceType, powerLocationId],
    !!(selectedEquipment.type && (selectedEquipment.data?.inverterId || selectedEquipment.data?.id || powerLocationId)) // 선택된 장비가 있을 때만 활성화
  );

  // 환경센서 데이터 생성 (API 데이터 사용, temperatureConfig가 있을 때만)
  // sensor 1: 경사일사량(tIs1), 표면온도(tTemperature1)
  // sensor 2: 수평일사량(tIs2), 외기온도(tTemperature2)
  const generateEnvironmentSensorData = () => {
    if (!configData?.temperatureConfig) {
      return [];
    }
    return [
      {
        id: 'ENV-001',
        name: '환경센서',
        status: 'normal',
        tiltedIrradiance: temperatureData ? temperatureData.tIs1 : 0.00,
        horizontalIrradiance: temperatureData ? temperatureData.tIs2 : 0.00,
        outsideTemperature: temperatureData ? temperatureData.tTemperature2 : 0.0,
        surfaceTemperature: temperatureData ? temperatureData.tTemperature1 : 0.0
      }
    ];
  };

  // 환경센서 차트용 데이터 생성 (실제 API 데이터 사용)
  const generateEnvironmentChartData = () => {
    const data = [];
    
    // API 데이터가 있으면 실제 데이터 사용, 없으면 0으로 채운 데이터 생성
    if (hourlyTemperatureData.length > 0 || hourlySolarRadiationData.length > 0) {
      // 실제 API 데이터를 사용하여 24시간 데이터 생성
      for (let i = 0; i < 24; i++) {
        const hour = i;
        
        // 해당 시간의 온도 데이터 찾기 (현재 선택된 타입에 맞는 데이터)
        const tempData = hourlyTemperatureData.find(d => {
          const dataHour = new Date(d.hour).getHours();
          return dataHour === hour;
        });
        
        // 해당 시간의 일사량 데이터 찾기 (현재 선택된 타입에 맞는 데이터)
        const radiationData = hourlySolarRadiationData.find(d => {
          const dataHour = new Date(d.hour).getHours();
          return dataHour === hour;
        });
        
        data.push({
          time: hour.toString().padStart(2, '0'),
          hour: hour,
          outsideTemperature: tempData && selectedTemperatureType === 'outsideTemperature' ? tempData.value : 0.0,
          surfaceTemperature: tempData && selectedTemperatureType === 'surfaceTemperature' ? tempData.value : 0.0,
          tiltedIrradiance: radiationData && selectedIrradianceType === 'tiltedIrradiance' ? radiationData.value : 0.0,
          horizontalIrradiance: radiationData && selectedIrradianceType === 'horizontalIrradiance' ? radiationData.value : 0.0
        });
      }
    } else {
      // API 데이터가 없을 때: 모든 값을 0으로 설정
      for (let i = 0; i < 24; i++) {
        const hour = i;
        data.push({
          time: hour.toString().padStart(2, '0'),
          hour: hour,
          outsideTemperature: 0.0,
          surfaceTemperature: 0.0,
          tiltedIrradiance: 0.0,
          horizontalIrradiance: 0.0
        });
      }
    }
    
    return data;
  };


  useEffect(() => {
    // API 데이터가 있으면 사용
    if (chartData.length > 0) {
      const processedData = chartData.map(item => ({
        time: item.hour.toString().padStart(2, '0'),
        hour: item.hour,
        value: item.value,
        createdAt: item.createdAt
      }));
      setTrendData(processedData);
    } else {
      setTrendData([]);
    }
  }, [chartData]);

  // 설정 데이터에서 인버터 정보 가져오기 (API에서 가져온 데이터 사용)
  const inverters = useMemo(() => {
    return configData?.inverterConfigs?.map(config => ({
      id: config.inverterId.toString(),
      inverterId: config.id, // config.id를 사용 (inverter_config의 ID)
      name: config.name,
      status: 'normal', // 기본값
      voltage: 0, // API 연결 안 되면 0으로 표시
      current: 0,
      power: 0,
      frequency: 0,
      dcVoltage: 0,
      dcCurrent: 0,
      dcPower: 0,
      acVoltageR: 0,
      acVoltageS: 0,
      acVoltageT: 0,
      acCurrentR: 0,
      acCurrentS: 0,
      acCurrentT: 0,
      acPowerR: 0,
      acPowerS: 0,
      acPowerT: 0
    })) || [];
  }, [configData?.inverterConfigs]);

  // 설정 데이터에서 접속반 정보 가져오기 (패널 API 데이터를 접속반으로 사용)
  const connectionBoxes = useMemo(() => {
    return configData?.panelConfigs?.map(config => ({
      id: config.id.toString(),
      panelId: config.panelId,
      name: config.name,
      status: 'normal' // 기본값
    })) || [];
  }, [configData?.panelConfigs]);
  
  // 환경센서 데이터를 매번 새로 생성 (temperatureData가 업데이트될 때마다)
  const environmentSensors = generateEnvironmentSensorData();

  // 첫 번째 인버터를 기본 선택 (초기 설정만, 주기적 fetch는 usePeriodicFetch가 처리)
  useEffect(() => {
    if (inverters.length > 0 && !selectedEquipment.data) {
      setSelectedEquipment({ type: 'inverter', data: inverters[0] });
    }
  }, [inverters, selectedEquipment.data]);

  // 첫 번째 접속반을 기본 선택 (인버터가 없을 때, 초기 설정만)
  useEffect(() => {
    if (inverters.length === 0 && connectionBoxes.length > 0 && !selectedEquipment.data) {
      setSelectedEquipment({ type: 'connectionBox', data: connectionBoxes[0] });
    }
  }, [inverters.length, connectionBoxes, selectedEquipment.data]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'normal': return '#10B981';
      case 'error': return '#EF4444';
      case 'warning': return '#F59E0B';
      default: return '#6B7280';
    }
  };

  // 동작상태(run) 표시: RUN/run/1/정상 -> 정상, 그 외 -> 정지
  const isInverterRun = (run) => {
    if (run === 1 || run === '1') return true;
    if (run === '정상') return true;
    if (typeof run === 'string' && run.trim().toUpperCase() === 'RUN') return true; // 'run' 포함
    return false;
  };

  const getInverterRunText = (run) => (isInverterRun(run) ? '정상' : '정지');



  const dataTypeOptions = [
    { value: 'dcVoltage', label: 'DC 전압' },
    { value: 'dcCurrent', label: 'DC 전류' },
    { value: 'dcPower', label: 'DC 전력' },
    { value: 'acVoltage', label: 'AC 전압' },
    { value: 'acCurrent', label: 'AC 전류' },
    { value: 'acPower', label: 'AC 전력' }
  ];

  const getDataTypeValue = (data, type) => {
    switch (type) {
      case 'dcVoltage': return data.dcVoltage;
      case 'dcCurrent': return data.dcCurrent;
      case 'dcPower': return data.dcPower;
      case 'acVoltage': return (data.acVoltageR + data.acVoltageS + data.acVoltageT) / 3;
      case 'acCurrent': return (data.acCurrentR + data.acCurrentS + data.acCurrentT);
      case 'acPower': return (data.acPowerR + data.acPowerS + data.acPowerT) / 3;
      default: return 0;
    }
  };

  // const getDataTypeUnit = (type) => {
  //   if (type === 'dcVoltage' || type === 'acVoltage') return 'V';
  //   if (type === 'dcCurrent' || type === 'acCurrent') return 'A';
  //   if (type === 'dcPower' || type === 'acPower') return 'kW';
  //   return '';
  // };

  // 드롭다운용 전체 장비 목록 생성
  const allEquipmentOptions = useMemo(() => {
    const options = [];
    // 인버터
    inverters.forEach(inverter => {
      options.push({
        value: `inverter-${inverter.id}`,
        label: `[인버터] ${inverter.name} - ${inverter.id}`,
        type: 'inverter',
        data: inverter
      });
    });
    // 접속반
    connectionBoxes.forEach(box => {
      options.push({
        value: `connectionBox-${box.id}`,
        label: `[접속반] ${box.name} - ${box.panelId}`,
        type: 'connectionBox',
        data: box
      });
    });
    // 환경센서
    environmentSensors.forEach(sensor => {
      options.push({
        value: `environmentSensor-${sensor.id}`,
        label: `[환경센서] ${sensor.name}`,
        type: 'environmentSensor',
        data: sensor
      });
    });
    return options;
  }, [inverters, connectionBoxes, environmentSensors]);

  // 드롭다운 선택 값
  const dropdownValue = useMemo(() => {
    if (!selectedEquipment.type || !selectedEquipment.data) return '';
    const type = selectedEquipment.type;
    const id = selectedEquipment.data.id;
    return `${type}-${id}`;
  }, [selectedEquipment]);

  // 드롭다운 변경 핸들러
  const handleDropdownChange = (e) => {
    const value = e.target.value;
    if (!value) return;
    
    const option = allEquipmentOptions.find(opt => opt.value === value);
    if (!option) return;

    if (option.type === 'inverter') {
      setSelectedEquipment({ type: 'inverter', data: option.data });
      setSelectedEnvironmentSensor(null);
      if (option.data.inverterId) {
        fetchInverterData(option.data.inverterId);
        const backendType = getBackendChartType(selectedDataType);
        fetchChartData(option.data.inverterId, backendType);
        fetchInverterErrorCount(option.data.inverterId);
      }
    } else if (option.type === 'connectionBox') {
      setSelectedEquipment({ type: 'connectionBox', data: option.data });
      setSelectedEnvironmentSensor(null);
      if (option.data.id) {
        fetchChannelData(option.data.id);
      }
    } else if (option.type === 'environmentSensor') {
      setSelectedEquipment({ type: 'environmentSensor', data: option.data });
      setSelectedEnvironmentSensor(option.data);
      if (powerLocationId) {
        fetchTemperatureData(powerLocationId);
      }
    }
  };

  return (
    <div className="power-plant-trend-detail">
      {/* 모바일용 장비 선택 드롭다운 */}
      <div className="mobile-equipment-select">
        <select 
          value={dropdownValue}
          onChange={handleDropdownChange}
          className="equipment-dropdown"
        >
          <option value="">장비를 선택하세요</option>
          {allEquipmentOptions.map(option => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      <div className="trend-layout">
        {/* 왼쪽 패널 - 장비 목록 */}
        <div className="left-panel">
          {/* 인버터 섹션 */}
          <div className="equipment-section">
            <h3>인버터</h3>
            {inverters.map((inverter) => (
              <div 
                key={inverter.id} 
                className={`trend-equipment-item ${selectedEquipment.type === 'inverter' && selectedEquipment.data?.id === inverter.id ? 'selected' : ''}`}
                onClick={() => {
                  console.log('인버터 클릭:', inverter);
                  setSelectedEquipment({ type: 'inverter', data: inverter });
                  setSelectedEnvironmentSensor(null); // 환경센서 선택 초기화
                  // 인버터 ID로 실제 데이터 가져오기
                  if (inverter.inverterId) {
                    console.log('인버터 ID로 API 호출:', inverter.inverterId);
                    fetchInverterData(inverter.inverterId);
                    // 차트 데이터도 함께 가져오기
                    const backendType = getBackendChartType(selectedDataType);
                    fetchChartData(inverter.inverterId, backendType);
                    // 에러 카운트도 함께 가져오기
                    fetchInverterErrorCount(inverter.inverterId);
                  } else {
                    console.log('인버터 ID가 없음:', inverter);
                  }
                }}
              >
                {inverter.name} - {inverter.id}
              </div>
            ))}
          </div>

          {/* 접속반 섹션 */}
          {connectionBoxes.length > 0 && (
          <div className="equipment-section">
            <h3>접속반</h3>
            {connectionBoxes.map((box) => (
              <div 
                key={box.id} 
                className={`trend-equipment-item ${selectedEquipment.type === 'connectionBox' && selectedEquipment.data?.id === box.id ? 'selected' : ''}`}
                onClick={() => {
                  console.log('접속반 클릭:', box);
                  setSelectedEquipment({ type: 'connectionBox', data: box });
                  setSelectedEnvironmentSensor(null); // 환경센서 선택 초기화
                  // 접속반 ID로 실제 채널 데이터 가져오기
                  if (box.id) {
                    console.log('접속반 ID로 채널 데이터 API 호출:', box.id);
                    fetchChannelData(box.id);
                  } else {
                    console.log('접속반 ID가 없음:', box);
                  }
                }}
              >
                {box.name} - {box.panelId}
              </div>
            ))}
          </div>
          )}

          {/* 환경센서 섹션 */}
          {environmentSensors.length > 0 && (
          <div className="equipment-section">
            <h3>환경센서</h3>
            {environmentSensors.map((sensor) => (
              <div 
                key={sensor.id} 
                className={`trend-equipment-item ${selectedEquipment.type === 'environmentSensor' && selectedEquipment.data?.id === sensor.id ? 'selected' : ''}`}
                onClick={() => {
                  setSelectedEquipment({ type: 'environmentSensor', data: sensor });
                  setSelectedEnvironmentSensor(sensor);
                  // 환경센서 선택 시 온도 데이터 가져오기
                  if (powerLocationId) {
                    fetchTemperatureData(powerLocationId);
                  }
                }}
              >
                {sensor.name}
              </div>
            ))}
          </div>
          )}
        </div>

        {/* 중앙 패널 - 장비 상세 정보 */}
        <div className="right-panel">
          {selectedEquipment.type === 'environmentSensor' ? (() => {
            // 현재 선택된 환경센서의 최신 데이터를 environmentSensors에서 찾기
            const currentSensor = environmentSensors.find(s => s.id === selectedEquipment.data?.id) || environmentSensors[0];
            
            return (
            <>
              {/* 상단: 환경센서 테이블과 외기온도 차트 */}
              <div className="environment-top-container">
                {/* 환경센서 데이터 테이블 */}
                <div className="environment-sensor-table">
                  <h4>환경센서</h4>
                  <table className="environment-data-table">
                    <thead>
                      <tr>
                        <th>항목</th>
                        <th>데이터</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>경사일사량</td>
                        <td>{currentSensor ? Math.round(currentSensor.tiltedIrradiance || 0).toLocaleString() : '0'} W/m²</td>
                      </tr>
                      <tr>
                        <td>수평일사량</td>
                        <td>{currentSensor ? Math.round(currentSensor.horizontalIrradiance || 0).toLocaleString() : '0'} W/m²</td>
                      </tr>
                      <tr>
                        <td>외기온도</td>
                        <td>{currentSensor ? (currentSensor.outsideTemperature || 0).toFixed(1) : '0.0'} °C</td>
                      </tr>
                      <tr>
                        <td>표면온도</td>
                        <td>{currentSensor ? (currentSensor.surfaceTemperature || 0).toFixed(1) : '0.0'} °C</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* 외기온도/표면온도 차트 */}
                <div className="environment-chart">
                  <div className="chart-header">
                    <select 
                      className="chart-select"
                      value={selectedTemperatureType}
                      onChange={(e) => setSelectedTemperatureType(e.target.value)}
                    >
                      <option value="outsideTemperature">외기온도</option>
                      <option value="surfaceTemperature">표면온도</option>
                    </select>
                    <div className="chart-legend">
                      <div className="legend-item">
                        <div className="legend-color" style={{backgroundColor: '#3b82f6'}}></div>
                        <span>{selectedTemperatureType === 'outsideTemperature' ? '외기온도' : '표면온도'}</span>
                      </div>
                    </div>
                  </div>
                  <div className="chart-content">
                    <svg width="100%" height="250" viewBox="0 0 1200 250">
                      {/* Y축 그리드 라인 */}
                      {(() => {
                        const data = generateEnvironmentChartData();
                        const values = data.map(d => selectedTemperatureType === 'outsideTemperature' 
                          ? d.outsideTemperature 
                          : d.surfaceTemperature);
                        const maxValue = Math.max(...values);
                        const minValue = Math.min(...values);
                        
                        // 5도 간격으로 그리드 생성
                        const minGrid = Math.floor(minValue / 5) * 5;
                        const maxGrid = Math.ceil(maxValue / 5) * 5;
                        const gridValues = [];
                        for (let i = minGrid; i <= maxGrid; i += 5) {
                          gridValues.push(i);
                        }
                        
                        return gridValues.map((value, index) => {
                          const ratio = (value - minGrid) / (maxGrid - minGrid);
                          const y = 20 + (1 - ratio) * 180;
                          return (
                            <g key={index}>
                              <line x1="50" y1={y} x2="1150" y2={y} stroke="#e5e7eb" strokeWidth="1"/>
                              <text x="40" y={y + 4} textAnchor="end" fontSize="12" fill="#6b7280">
                                {value}°C
                              </text>
                            </g>
                          );
                        });
                      })()}
                      
                      {/* X축 시간 라벨 */}
                      {Array.from({length: 24}, (_, i) => {
                        const x = 50 + (i * 45);
                        const cellCenterX = x + 22.5; // 그리드 셀의 중간 (45/2 = 22.5)
                        return (
                          <g key={i}>
                            <line x1={x} y1="20" x2={x} y2="200" stroke="#e5e7eb" strokeWidth="0.5"/>
                            <text x={cellCenterX} y="215" textAnchor="middle" fontSize="10" fill="#6b7280">
                              {i.toString().padStart(2, '0')}
                            </text>
                          </g>
                        );
                      })}
                      
                      {/* 온도 막대 그래프 */}
                      {(() => {
                        const data = generateEnvironmentChartData();
                        const values = data.map(d => selectedTemperatureType === 'outsideTemperature' 
                          ? d.outsideTemperature 
                          : d.surfaceTemperature);
                        const maxValue = Math.max(...values);
                        const minValue = Math.min(...values);
                        
                        // 5도 간격으로 그리드 범위 계산
                        const minGrid = Math.floor(minValue / 5) * 5;
                        const maxGrid = Math.ceil(maxValue / 5) * 5;
                        const range = maxGrid - minGrid || 1;
                        
                        return data.map((d, index) => {
                          const value = selectedTemperatureType === 'outsideTemperature' 
                            ? d.outsideTemperature 
                            : d.surfaceTemperature;
                          const normalizedValue = (value - minGrid) / range;
                          const height = normalizedValue * 180;
                          const x = 50 + (index * 45) + 15;
                          const y = 200 - height;
                          return (
                            <rect
                              key={index}
                              x={x}
                              y={y}
                              width="15"
                              height={height}
                              fill="#3b82f6"
                              opacity="0.8"
                            />
                          );
                        });
                      })()}
                    </svg>
                  </div>
                </div>
              </div>

              {/* 하단: 경사일사량/수평일사량 차트 (전체 너비) */}
              <div className="environment-bottom-container">
                <div className="environment-chart-full">
                  <div className="chart-header">
                    <select 
                      className="chart-select"
                      value={selectedIrradianceType}
                      onChange={(e) => setSelectedIrradianceType(e.target.value)}
                    >
                      <option value="tiltedIrradiance">경사일사량</option>
                      <option value="horizontalIrradiance">수평일사량</option>
                    </select>
                    <div className="chart-legend">
                      <div className="legend-item">
                        <div className="legend-color" style={{backgroundColor: '#3b82f6'}}></div>
                        <span>{selectedIrradianceType === 'tiltedIrradiance' ? '경사일사량' : '수평일사량'}</span>
                      </div>
                    </div>
                  </div>
                  <div className="chart-content">
                    <svg width="100%" height="250" viewBox="0 0 1200 250">
                      {/* Y축 그리드 라인 */}
                      {(() => {
                        const data = generateEnvironmentChartData();
                        const values = data.map(d => selectedIrradianceType === 'tiltedIrradiance' 
                          ? d.tiltedIrradiance 
                          : d.horizontalIrradiance);
                        const maxValue = Math.max(...values);
                        const minValue = Math.min(...values);
                        
                        // 100단위로 그리드 생성
                        const minGrid = Math.floor(minValue / 100) * 100;
                        const maxGrid = Math.ceil(maxValue / 100) * 100;
                        const gridValues = [];
                        for (let i = minGrid; i <= maxGrid; i += 100) {
                          gridValues.push(i);
                        }
                        
                        return gridValues.map((value, index) => {
                          const ratio = (value - minGrid) / (maxGrid - minGrid);
                          const y = 20 + (1 - ratio) * 180;
                          return (
                            <g key={index}>
                              <line x1="50" y1={y} x2="1150" y2={y} stroke="#e5e7eb" strokeWidth="1"/>
                              <text x="40" y={y + 4} textAnchor="end" fontSize="12" fill="#6b7280">
                                {value}W/m²
                              </text>
                            </g>
                          );
                        });
                      })()}
                      
                      {/* X축 시간 라벨 */}
                      {Array.from({length: 24}, (_, i) => {
                        const x = 50 + (i * 45);
                        const cellCenterX = x + 22.5; // 그리드 셀의 중간 (45/2 = 22.5)
                        return (
                          <g key={i}>
                            <line x1={x} y1="20" x2={x} y2="200" stroke="#e5e7eb" strokeWidth="0.5"/>
                            <text x={cellCenterX} y="215" textAnchor="middle" fontSize="10" fill="#6b7280">
                              {i.toString().padStart(2, '0')}
                            </text>
                          </g>
                        );
                      })}
                      
                      {/* 일사량 막대 그래프 */}
                      {(() => {
                        const data = generateEnvironmentChartData();
                        const values = data.map(d => selectedIrradianceType === 'tiltedIrradiance' 
                          ? d.tiltedIrradiance 
                          : d.horizontalIrradiance);
                        const maxValue = Math.max(...values);
                        const minValue = Math.min(...values);
                        
                        // 100단위로 그리드 범위 계산
                        const minGrid = Math.floor(minValue / 100) * 100;
                        const maxGrid = Math.ceil(maxValue / 100) * 100;
                        const range = maxGrid - minGrid || 1;
                        
                        return data.map((d, index) => {
                          const value = selectedIrradianceType === 'tiltedIrradiance' 
                            ? d.tiltedIrradiance 
                            : d.horizontalIrradiance;
                          const normalizedValue = (value - minGrid) / range;
                          const height = normalizedValue * 180;
                          const x = 50 + (index * 45) + 15;
                          const y = 200 - height;
                          return (
                            <rect
                              key={index}
                              x={x}
                              y={y}
                              width="15"
                              height={height}
                              fill="#3b82f6"
                              opacity="0.8"
                            />
                          );
                        });
                      })()}
                    </svg>
                  </div>
                </div>
              </div>
            </>
            );
          })() : selectedEquipment.type === 'inverter' ? (
            <>
              {/* 상단 테이블과 상태 정보 */}
              <div className="top-table-container">
                <div className="dc-ac-table">
                  <h4>인버터 &gt; {selectedEquipment.data.name}</h4>
                  <table className="trend-data-table">
                    <thead>
                      <tr>
                        <th rowSpan="2">항목</th>
                        <th rowSpan="2">DC</th>
                        <th colSpan="4">AC</th>
                      </tr>
                      <tr>
                        <th>R</th>
                        <th>S</th>
                        <th>T</th>
                        <th>종합</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>전압</td>
                        <td>{inverterRealData ? inverterRealData.pvVoltage.toFixed(1) : '0.0'}</td>
                        <td>{inverterRealData ? inverterRealData.rs.toFixed(1) : '0.0'}</td>
                        <td>{inverterRealData ? inverterRealData.st.toFixed(1) : '0.0'}</td>
                        <td>{inverterRealData ? inverterRealData.tr.toFixed(1) : '0.0'}</td>
                        <td>{inverterRealData ? 
                          ((inverterRealData.rs + inverterRealData.st + inverterRealData.tr) / 3).toFixed(1) : 
                          '0.0'
                        }</td>
                      </tr>
                      <tr>
                        <td>전류</td>
                        <td>{inverterRealData ? inverterRealData.pvCurrent.toFixed(1) : '0.0'}</td>
                        <td>{inverterRealData ? inverterRealData.ra.toFixed(1) : '0.0'}</td>
                        <td>{inverterRealData ? inverterRealData.sa.toFixed(1) : '0.0'}</td>
                        <td>{inverterRealData ? inverterRealData.ta.toFixed(1) : '0.0'}</td>
                        <td>{inverterRealData ? 
                          (inverterRealData.ra + inverterRealData.sa + inverterRealData.ta).toFixed(1) : 
                          '0.0'
                        }</td>
                      </tr>
                      <tr>
                        <td>전력</td>
                        <td>{inverterRealData ? inverterRealData.pvPower.toFixed(1) : '0.0'}</td>
                        <td>{inverterRealData ? inverterRealData.rPower.toFixed(1) : '0.0'}</td>
                        <td>{inverterRealData ? inverterRealData.sPower.toFixed(1) : '0.0'}</td>
                        <td>{inverterRealData ? inverterRealData.tPower.toFixed(1) : '0.0'}</td>
                        <td>{inverterRealData ? 
                          (Number(inverterRealData.generatedPower) || 0).toFixed(1) : 
                          '0.0'
                        }</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                
                {/* 상태 정보를 표 오른쪽에 배치 */}
                <div className="status-info">
                  <h4>상태 정보</h4>
                  <div className="status-grid">
                    <div className="status-item">
                      <span className="status-label">주파수</span>
                      <span className="status-value">{inverterRealData ? inverterRealData.gf.toFixed(1) : '0.0'} Hz</span>
                    </div>
                    <div className="status-item">
                      <span className="status-label">동작상태</span>
                      <span 
                        className="status-value"
                        style={{ color: inverterRealData ? getStatusColor(selectedEquipment.data.status) : '#EF4444' }}
                      >
                        {inverterRealData ? getInverterRunText(inverterRealData.run) : '연결끊김'}
                      </span>
                    </div>
                    <div className="status-item">
                      <span className="status-label">에러</span>
                      <span className="status-value" style={{ color: inverterErrorCount > 0 ? '#EF4444' : '#10B981' }}>
                        {inverterErrorCount}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* 24시간 막대 그래프 */}
              <div className="chart-section">
                {trendData && (
                  <div className="bar-chart-container">
                    <div className="bar-chart">
                      <svg width="100%" height="350" viewBox="0 0 1500 600">
                        {/* 데이터 선택 박스 */}
                        <foreignObject x="40" y="40" width="280" height="45">
                  <select 
                    value={selectedDataType} 
                    onChange={(e) => setSelectedDataType(e.target.value)}
                            style={{
                              width: '100%',
                              padding: '8px 12px',
                              border: '2px solid #d1d5db',
                              borderRadius: '8px',
                              fontSize: '14px',
                              background: 'white',
                              cursor: 'pointer',
                              fontWeight: '500',
                              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
                            }}
                  >
                    {dataTypeOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                        </foreignObject>

                        {/* 그리드 라인 */}
                        <defs>
                          <pattern id="bar-grid" width="36" height="56" patternUnits="userSpaceOnUse">
                            <path d="M 36 0 L 0 0 0 56" fill="none" stroke="#e5e7eb" strokeWidth="0.5"/>
                          </pattern>
                        </defs>
                        
                        {/* 차트 영역 배경 */}
                        <rect x="50" y="120" width="1400" height="400" fill="url(#bar-grid)" stroke="#d1d5db" strokeWidth="1"/>
                        
                        {/* Y축 그리드 라인 및 라벨 */}
                        {[0, 0.25, 0.5, 0.75, 1].map((ratio, index) => {
                          const maxValue = chartData.length > 0 
                            ? Math.max(...chartData.map(d => d.value))
                            : Math.max(...trendData.map(d => getDataTypeValue(d, selectedDataType)));
                          const value = maxValue * ratio;
                          const y = 120 + (1 - ratio) * 400;
                          return (
                            <g key={index}>
                              <line
                                x1="50"
                                y1={y}
                                x2="1450"
                                y2={y}
                                stroke="#d1d5db"
                                strokeWidth="1"
                              />
                              <text
                                x="40"
                                y={y + 6}
                                textAnchor="end"
                                fontSize="16"
                                fill="#6b7280"
                              >
                                {value.toFixed(0)}
                              </text>
                            </g>
                          );
                        })}

                        {/* X축 그리드 라인 및 라벨 (시간) */}
                        {trendData.map((d, index) => {
                          const cellWidth = 58;
                          const x = 50 + (index * cellWidth);
                          // 그리드 셀의 중앙에 라벨 배치
                          const cellCenterX = x + cellWidth / 2; // 58픽셀 그리드 셀의 중앙
                          return (
                            <g key={index}>
                              <line
                                x1={x}
                                y1="120"
                                x2={x}
                                y2="520"
                                stroke="#d1d5db"
                                strokeWidth="0.5"
                              />
                            <text
                                x={cellCenterX}
                                y="540"
                              textAnchor="middle"
                                fontSize="15"
                              fill="#6b7280"
                            >
                              {d.time}
                            </text>
                            </g>
                          );
                        })}

                        {/* 막대 그래프 */}
                        {trendData.map((d, index) => {
                          const value = chartData.length > 0 ? d.value : getDataTypeValue(d, selectedDataType);
                          const maxValue = chartData.length > 0 
                            ? Math.max(...chartData.map(data => data.value))
                            : Math.max(...trendData.map(data => getDataTypeValue(data, selectedDataType)));
                          const height = maxValue > 0 ? (value / maxValue) * 400 : 0;
                          // 24개 셀에 맞게 바 위치 계산 (각 셀 58픽셀)
                          const cellWidth = 58;
                          const barX = 50 + (index * cellWidth) + (cellWidth - 38) / 2; // 셀 중앙에 바 배치
                          const y = 120 + 400 - height;
                          const width = 38; // 바 너비
                          
                          return (
                            <g key={index}>
                              <rect
                                x={barX}
                                y={y}
                                width={width}
                                height={height}
                                fill="#3b82f6"
                                opacity="0.8"
                                className="bar"
                              />
                            </g>
                          );
                        })}
                      </svg>
                    </div>
                  </div>
                )}
              </div>
            </>
          ) : selectedEquipment.type === 'connectionBox' ? (
            <>
              {/* 접속반 채널 테이블 */}
              <div className="top-table-container">
                <div className="connection-box-table">
                  <h4>접속반 &gt; {selectedEquipment.data.name}</h4>
                  <table className="trend-data-table">
                    <thead>
                      <tr>
                        <th>채널 번호</th>
                        <th>전압(V)</th>
                        <th>전류(A)</th>
                        <th>전력(kW)</th>
                        <th>릴레이 상태</th>
                        <th>채널 상태</th>
                      </tr>
                    </thead>
                    <tbody>
                      {channelData.length > 0 ? (
                        channelData.map((channel) => (
                          <tr key={channel.channelNumber}>
                            <td>CH-{channel.channelNumber}</td>
                            <td>{channel.voltage.toFixed(1)}</td>
                            <td>{channel.current.toFixed(1)}</td>
                            <td>{channel.power.toFixed(1)}</td>
                            <td>
                              <span className={`relay-status ${channel.relay === 0 ? 'normal' : 'error'}`}>
                                {channel.relay}
                              </span>
                            </td>
                            <td>
                              <span className={`status-indicator ${channel.status === 0 ? 'normal' : 'error'}`}>
                                {channel.status === 0 ? '✅ 정상' : '❌ 이상'}
                              </span>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td 
                            colSpan="6" 
                            className={channelDataLoading ? '' : 'connection-disconnected'}
                            style={{ 
                              textAlign: 'center', 
                              padding: '20px', 
                              color: channelDataLoading ? '#6b7280' : '#EF4444'
                            }}
                          >
                            {channelDataLoading ? '채널 데이터를 불러오는 중...' : '접속반 연결 끊김'}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          ) : (
            <div className="no-selection">
              <p>왼쪽에서 인버터 또는 접속반을 선택해주세요</p>
            </div>
          )}
        </div>

      </div>
    </div>
  );
};

export default PowerPlantTrendDetail;
